import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import usePrefersReducedData from "../../hooks/usePrefersReducedData";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import styles from "./HeroVideoSection.module.css";

export type HeroVideoSectionProps = {
  videoSrc: string;
  mobileVideoSrc?: string;
  posterSrc: string;
  heading: string;
  description: string;
  ctaLabel: string;
  mapTargetId: string;
};

function HeroVideoSection({
  videoSrc,
  mobileVideoSrc,
  posterSrc,
  heading,
  description,
  ctaLabel,
  mapTargetId,
}: HeroVideoSectionProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const prefersReducedData = usePrefersReducedData();
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const mapTargetHref = mapTargetId.startsWith("#")
    ? mapTargetId
    : `#${mapTargetId}`;
  const shouldAutoplayVideo = !prefersReducedMotion && !prefersReducedData;
  const shouldRenderVideo = shouldAutoplayVideo && !hasVideoError;

  useEffect(() => {
    setIsVideoReady(false);
    setHasVideoError(false);
  }, [shouldAutoplayVideo, videoSrc]);

  useEffect(() => {
    if (
      !shouldAutoplayVideo ||
      !isVideoReady ||
      hasVideoError ||
      typeof document === "undefined"
    ) {
      return;
    }

    const syncPlayback = () => {
      if (!videoRef.current) {
        return;
      }

      if (document.hidden) {
        videoRef.current.pause();
        return;
      }

      void videoRef.current.play().catch(() => {
        // Autoplay can be blocked even when the media itself loaded successfully.
        // Keep the first frame/poster visible instead of removing the video.
      });
    };

    syncPlayback();
    document.addEventListener("visibilitychange", syncPlayback);

    return () => {
      document.removeEventListener("visibilitychange", syncPlayback);
    };
  }, [hasVideoError, isVideoReady, shouldAutoplayVideo]);

  const handleVideoReady = () => {
    setIsVideoReady(true);
  };

  const handleVideoError = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!event.currentTarget.error) {
      return;
    }

    setIsVideoReady(false);
    setHasVideoError(true);
  };

  return (
    <section
      className={styles.heroVideoSection}
      aria-labelledby="hero-video-title"
    >
      <div className={styles.mediaWrapper}>
        <div className={styles.mediaBackdrop} aria-hidden="true" />
        <img
          className={`${styles.poster} ${
            isVideoReady && !hasVideoError ? styles.posterHidden : ""
          }`}
          data-testid="hero-video-poster"
          src={posterSrc}
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority="high"
        />
        {shouldRenderVideo ? (
          <video
            ref={videoRef}
            className={`${styles.media} ${
              isVideoReady ? styles.mediaVisible : styles.mediaHidden
            }`}
            data-testid="hero-video-media"
            autoPlay={shouldAutoplayVideo}
            muted
            loop
            playsInline
            preload="metadata"
            poster={posterSrc}
            aria-hidden="true"
            onCanPlay={handleVideoReady}
            onLoadedData={handleVideoReady}
            onError={handleVideoError}
          >
            {mobileVideoSrc ? (
              <source
                src={mobileVideoSrc}
                type="video/mp4"
                media="(max-width: 768px)"
              />
            ) : null}
            <source src={videoSrc} type="video/mp4" />
          </video>
        ) : null}
        <div className={styles.scrim} aria-hidden="true" />

        <div className={styles.content}>
          <div className={styles.contentInner}>
            <h1 id="hero-video-title" className={styles.title}>
              {heading}
            </h1>
            <p className={styles.description}>{description}</p>
            <a className={styles.cta} href={mapTargetHref}>
              {ctaLabel}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroVideoSection;
